import { DocAuditEntry } from '@/types/documents'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import queryKeys from '../query-keys'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import buildService from '../create-service'

const service = buildService<DocAuditEntry>({
  pathCategory: 'doc_audit_entries',
})

export function useGetDocAuditEntry({
  id,
  ...options
}: UseGetDetailOptions<DocAuditEntry>) {
  const query = useGetDetail<DocAuditEntry>({
    id,
    queryKey: queryKeys.docAuditEntries.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    docAuditEntry: query.data,
    ...query,
  }
}

export function useCreateDocAuditEntry(
  options: UseCreateDetailOptions<DocAuditEntry> = {},
) {
  const mutation = useCreateDetail<DocAuditEntry>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createDocAuditEntry: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateDocAuditEntry(
  options: UseUpdateDetailOptions<DocAuditEntry> = {},
) {
  const mutation = useUpdateDetail<DocAuditEntry>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateDocAuditEntry: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocAuditEntry(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<DocAuditEntry>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteDocAuditEntry: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetDocAuditEntries({
  filters,
  ...options
}: UseGetListOptions<DocAuditEntry> = {}) {
  const query = useGetList<DocAuditEntry>({
    filters,
    queryKey: queryKeys.docAuditEntries.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    docAuditEntries: query.allData,
    ...query,
  }
}

export function useUpdateDocAuditEntries(
  options: UseUpdateListOptions<DocAuditEntry> = {},
) {
  const mutation = useUpdateList<DocAuditEntry>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateDocAuditEntries: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocAuditEntries({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<DocAuditEntry>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteDocAuditEntries: mutation.mutateAsync,
    ...mutation,
  }
}
